import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid2,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LinkElement, MediaElement, TypographyElement, useContentElement,
} from '@plugins/next-cms-core';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import defaults from 'lodash/defaults';
import isNull from 'lodash/isNull';
import omitBy from 'lodash/omitBy';
import { FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import PageInsetTop from '@components/atoms/PageInsetTop';
import { useTranslation } from 'next-i18next';
import Slider from '@components/atoms/Slider';
import Container from '@components/atoms/Container';
import Image from '@components/atoms/Image2';
import Link from '@components/atoms/Link';
import { useRouter } from 'next/router';
import { ENABLE_FUECHSE_BERLIN_PROMOTION } from '@/constants';

const HEADER_MAX_HEIGHT = 800;
const DEFER_SLIDER_TIMEOUT = 10000;

function PageHeaderBlock(props) {
  const {
    data,
    context: { page },
  } = props;
  const router = useRouter();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [useSlider, setUseSlider] = useState(false);
  const [swiper, setSwiper] = useState();
  const [canGoPrevious, setCanGoPrevious] = useState(false);
  const [canGoNext, setCanGoNext] = useState(true);
  const isTopContentOffset = page.isTopContentOffset || page.attributes?.isTopContentOffset;
  const [slideIndex, setSlideIndex] = useState(0);
  const { elementData } = useContentElement(
    data,
    PageHeaderBlock.dataSchema,
  );

  const {
    pageHeaderSlides,
    enablePromotionDisplay,
    isContained,
    pageHeaderVisibility: visibility,
    enableAutoplay,
  } = elementData;
  let { height } = elementData;
  const maxWidth = '1500px';
  // eslint-disable-next-line no-underscore-dangle
  const isPartiallyCoveredByOtherElement = page?.content?.find((c) => c.__typename === 'ComponentContentCarSearchFilter')?.isTranslatedY;

  if (!height || height <= 0) {
    height = HEADER_MAX_HEIGHT;
  }

  const promotion1aAutoservice = useMemo(() => get1aAutoservicePromotion(router, t), [router]);

  const handleGoToPreviousPage = useCallback(() => {
    setUseSlider(true);
    swiper?.slidePrev();
  }, [swiper, setUseSlider]);
  const handleGoToNextPage = useCallback(() => {
    setUseSlider(true);
    swiper?.slideNext();
  }, [swiper, setUseSlider]);

  const handleSlideChange = useCallback(() => {
    setSlideIndex(swiper.activeIndex);
    setCanGoPrevious(swiper.activeIndex > 0);
    setCanGoNext(swiper.activeIndex < swiper.snapGrid.length - 1);
  }, [
    swiper,
    setSlideIndex,
    setCanGoPrevious,
    setCanGoNext,
  ]);

  const display = {
    xs: 'flex',
    md: 'flex',
  };
  const displayPageOffset = { xs: 'none' };

  switch (visibility) {
    case 'desktop':
      display.xs = 'none';
      display.md = 'flex';
      displayPageOffset.xs = 'none';
      displayPageOffset.md = 'none';
      break;
    case 'mobile':
      display.xs = 'flex';
      display.md = 'none';
      displayPageOffset.xs = 'block';
      displayPageOffset.md = isTopContentOffset ? 'none' : 'block';
      break;
    default:
      displayPageOffset.xs = 'block';
      displayPageOffset.md = 'none';
      break;
  }

  useEffect(() => {
    const tm = setTimeout(() => {
      setUseSlider(true);
    }, DEFER_SLIDER_TIMEOUT);
    const interactionHandler = () => {
      setUseSlider(true);
      window.removeEventListener('mousemove', interactionHandler);
      window.removeEventListener('scroll', interactionHandler);
    };

    window.addEventListener('mousemove', interactionHandler);
    window.addEventListener('scroll', interactionHandler);

    return () => {
      clearTimeout(tm);
      window.removeEventListener('mousemove', interactionHandler);
      window.removeEventListener('scroll', interactionHandler);
    };
  }, [setUseSlider]);

  return (
    <>
      <PageInsetTop
        sx={{ display: displayPageOffset }}
      />
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display,
          minHeight: {
            xs: 0,
            md: height,
          },
        }}
      >
        {isContained && (
          <Box
            sx={{
              width: `calc((100% - ${maxWidth}) / 2 + 150px)`,
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 2,
              pointerEvents: 'none',
              background: 'linear-gradient(to right, #000 calc(100% - 150px), transparent)',
              height: '100%',
            }}
          />
        )}
        {isContained && (
          <Box
            sx={{
              width: `calc((100% - ${maxWidth}) / 2 + 150px)`,
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 2,
              pointerEvents: 'none',
              background: 'linear-gradient(to left, #000 calc(100% - 150px), transparent)',
              height: '100%',
            }}
          />
        )}
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            flexDirection: 'column',
            alignItems: 'center',
            margin: '0 auto',
            display,
            minHeight: {
              xs: 0,
              md: height,
            },
            maxWidth: isContained ? maxWidth : undefined,
          }}
        >
          {pageHeaderSlides.length > 1 && canGoPrevious && (
            <Button
              aria-label="previous"
              disabled={!canGoPrevious}
              onClick={handleGoToPreviousPage}
              size="small"
              sx={{
                fontSize: theme.typography.pxToRem(45),
                position: 'absolute',
                display: {
                  xs: 'none',
                  md: 'block',
                },
                minWidth: 0,
                top: '50%',
                zIndex: 2,
                color: 'white',
                backgroundColor: 'transparent',
                '&.Mui-disabled': {
                  color: 'white',
                },
                '& .MuiTouchRipple-root': {
                  display: 'none',
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                left: theme.spacing(10),
                '& svg': {
                  filter: 'drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.4))',
                },
                '& svg:nth-of-type(2)': {
                  marginLeft: theme.spacing(-4.5),
                  display: 'none',
                },
                '&:hover svg:nth-of-type(2)': {
                  display: 'inline',
                },
              }}
            >
              <FiChevronLeft />
              <FiChevronLeft />
            </Button>
          )}
          {!useSlider ? (
            <Box
              sx={{
                width: '100%',
                minHeight: {
                  xs: 0,
                  md: height,
                },
              }}
            >
              {pageHeaderSlides.length > 0 && (
                <Slide
                  enablePriority
                  height={height}
                  index={0}
                  isMobile={isSmall}
                  isPartiallyCoveredByOtherElement={isPartiallyCoveredByOtherElement}
                  item={pageHeaderSlides[0]}
                  items={pageHeaderSlides}
                  theme={theme}
                />
              )}
            </Box>
          ) : (
            <Slider
              allowTouchMove={pageHeaderSlides.length > 1}
              autoHeight
              autoplay={(pageHeaderSlides.length > 1 && enableAutoplay) ? { delay: 8000 } : false}
              handleSlideChange={handleSlideChange}
              hidePagination
              isButtonsVisible={false}
              items={pageHeaderSlides}
              setSwiper={setSwiper}
              SlideComponent={Slide}
              slideComponentProps={{
                height,
                items: pageHeaderSlides,
                theme,
                isMobile: isSmall,
                isPartiallyCoveredByOtherElement,
              }}
              sliderStyles={{
                width: '100%',
                minHeight: {
                  xs: 0,
                  md: height,
                },
              }}
              swiper={swiper}
              swiperSlideStyles={{
                height: 'auto',
                display: 'flex',
              }}
            />
          )}
          {pageHeaderSlides.length > 1 && canGoNext && (
            <Button
              aria-label="next"
              disabled={!canGoNext}
              onClick={handleGoToNextPage}
              size="small"
              sx={{
                fontSize: theme.typography.pxToRem(45),
                position: 'absolute',
                display: {
                  xs: 'none',
                  md: 'block',
                },
                minWidth: 0,
                top: '50%',
                zIndex: 3,
                color: 'white',
                backgroundColor: 'transparent',
                '&.Mui-disabled': {
                  color: 'white',
                },
                '& .MuiTouchRipple-root': {
                  display: 'none',
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                right: theme.spacing(10),
                '& svg': {
                  filter: 'drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.4))',
                },
                '& svg:first-child': {
                  marginRight: theme.spacing(-4.5),
                  display: 'none',
                },
                '&:hover svg:first-child': {
                  display: 'inline',
                },
              }}
            >
              <FiChevronRight />
              <FiChevronRight />
            </Button>
          )}
          {enablePromotionDisplay && (
            <>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    md: 'block',
                  },
                  pointerEvents: 'none',
                  position: 'absolute',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '100%',
                  height: '100%',
                  maxWidth: CONTENT_MAX_WIDTH,
                  zIndex: 2,
                  '& a': {
                    pointerEvents: 'all',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: isTopContentOffset ? 45 : 160,
                    right: theme.spacing(2),
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    borderRadius: `${theme.shape.borderRadius}px`,
                  }}
                >
                  {ENABLE_FUECHSE_BERLIN_PROMOTION && (
                    <Box
                      alignItems="center"
                      display="flex"
                    >
                      <Box
                        mr={2}
                        sx={{
                          '& img': {
                            width: 50,
                            height: 'auto',
                            [theme.breakpoints.up('md')]: {
                              width: 60,
                            },
                          },
                        }}
                      >
                        <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-fuechse`}>
                          <a>
                            <Image
                              alt="Füchse Berlin Logo"
                              height={60}
                              src="/images/promotion/fuechse-logo_small.png"
                              style={{ display: 'block' }}
                              width={60}
                            />
                          </a>
                        </Link>
                      </Box>
                      <Box>
                        <Typography
                          color="secondary"
                          dangerouslySetInnerHTML={{ __html: t('components.contentTypes.PageHeaderBlock.officialAutomotivePartner', { break: '<br />' }) }}
                          variant="caption"
                        />
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{
                      '& img': {
                        width: 50,
                        height: 'auto',
                        [theme.breakpoints.up('md')]: {
                          width: 70,
                        },
                      },
                    }}
                  >
                    <Box mb={1} textAlign="center">
                      <Typography color="secondary" sx={{ fontWeight: 'bold' }} variant="caption">
                        Ausgezeichnet von
                      </Typography>
                    </Box>
                    <Grid2 alignItems="center" container spacing={2}>
                      <Grid2 size={{ xs: 4 }}>
                        <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-mobilede`}>
                          <a>
                            <Image
                              alt="Mobile.De Logo"
                              height={39}
                              src="/images/promotion/mobile-de-logo.svg"
                              style={{ display: 'block' }}
                              width={164}
                            />
                          </a>
                        </Link>
                      </Grid2>
                      <Grid2 size={{ xs: 4 }}>
                        <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-ihk`}>
                          <a>
                            <Image
                              alt="IHK Logo"
                              height={280}
                              src="/images/promotion/ihk-logo.svg"
                              style={{ display: 'block' }}
                              width={561}
                            />
                          </a>
                        </Link>
                      </Grid2>
                      <Grid2 size={{ xs: 4 }}>
                        <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-automotivebusinessaward`}>
                          <a>
                            <Image
                              alt="KFZ Betrieb Logo"
                              height={68}
                              src="/images/promotion/kfz-gewerbe-logo.svg"
                              style={{ display: 'block' }}
                              width={274}
                            />
                          </a>
                        </Link>
                      </Grid2>
                    </Grid2>
                  </Box>
                </Box>
                {promotion1aAutoservice.isVisible && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: (isTopContentOffset ? 45 : 160) + 160,
                      left: theme.spacing(2),
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      paddingTop: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                      paddingLeft: theme.spacing(1),
                      paddingRight: theme.spacing(1),
                      borderRadius: `${theme.shape.borderRadius}px`,
                    }}
                  >
                    <Box
                      alignItems="center"
                      display="flex"
                    >
                      <Box
                        mr={2}
                        sx={{
                          '& img': {
                            width: 50,
                            height: 'auto',
                            borderRadius: '4px',
                            [theme.breakpoints.up('md')]: {
                              width: 70,
                            },
                          },
                        }}
                      >
                        <Link href={promotion1aAutoservice.url}>
                          <a>
                            <Image
                              alt="1a autoservice"
                              height={70}
                              src="/images/promotion/1a-autoservice-logo.svg"
                              style={{ display: 'block' }}
                              width={70}
                            />
                          </a>
                        </Link>
                      </Box>
                      <Box>
                        <Typography
                          color="secondary"
                          dangerouslySetInnerHTML={{ __html: t('components.contentTypes.PageHeaderBlock.1aAutomotiveTitle', { break: '<br />' }) }}
                          variant="subtitle2"
                        />
                        <Typography
                          color="secondary"
                          dangerouslySetInnerHTML={{ __html: t('components.contentTypes.PageHeaderBlock.1aAutomotiveSlogan', { break: '<br />' }) }}
                          variant="caption"
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              {pageHeaderSlides[slideIndex]?.disclaimer && (
                <Box
                  sx={{
                    display: {
                      xs: 'block',
                      md: 'none',
                    },
                    backdropFilter: 'saturate(180%) blur(3px)',
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                  }}
                >
                  <Container>
                    <TypographyElement
                      data={getDisclaimer(pageHeaderSlides[slideIndex]?.disclaimer)}
                      sx={{
                        lineHeight: '1.2em',
                        fontSize: '9px',
                      }}
                    />
                  </Container>
                </Box>
              )}
              <Box
                alignSelf="stretch"
                my={1}
                sx={{
                  display: {
                    xs: 'block',
                    md: 'none',
                  },
                }}
              >
                <Container>
                  <div>
                    <Accordion
                      slotProps={{
                        heading: {
                          component: 'div',
                        },
                      }}
                      variant="outlined"
                    >
                      <AccordionSummary
                        expandIcon={<FiChevronDown />}
                        sx={{
                          minHeight: 0,
                          '& .MuiAccordionSummary-content': {
                            fontSize: theme.typography.subtitle2.fontSize,
                            fontWeight: theme.typography.subtitle2.fontWeight,
                            display: 'flex',
                            alignItems: 'center',
                            mt: 1,
                            mb: 1,
                          },
                        }}
                      >
                        {ENABLE_FUECHSE_BERLIN_PROMOTION && (
                          <Image
                            alt="Füchse Berlin Logo"
                            height={20}
                            src="/images/promotion/fuechse-logo_small.png"
                            width={20}
                          />
                        )}
                        <Box ml={ENABLE_FUECHSE_BERLIN_PROMOTION ? 1 : 0}>
                          {t('components.contentTypes.PageHeaderBlock.partner')}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {ENABLE_FUECHSE_BERLIN_PROMOTION && (
                          <Box alignItems="center" display="flex">
                            <Box
                              mr={2}
                              sx={{
                                '& img': {
                                  width: 50,
                                  height: 'auto',
                                  [theme.breakpoints.up('md')]: {
                                    width: 70,
                                  },
                                },
                              }}
                            >
                              <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-fuechse`}>
                                <a>
                                  <Image
                                    alt="Füchse Berlin Logo"
                                    height={50}
                                    src="/images/promotion/fuechse-logo_small.png"
                                    width={50}
                                  />
                                </a>
                              </Link>
                            </Box>
                            <Box>
                              <Typography
                                dangerouslySetInnerHTML={{
                                  __html: t(
                                    'components.contentTypes.PageHeaderBlock.officialAutomotivePartner',
                                    { break: '' },
                                  ),
                                }}
                                variant="caption"
                              />
                            </Box>
                          </Box>
                        )}
                        <Box mt={1} p={1}>
                          <Box mb={1}>
                            <Typography sx={{ fontWeight: 'bold' }} variant="caption">
                              Ausgezeichnet von
                            </Typography>
                          </Box>
                          <Grid2 alignItems="center" container spacing={5}>
                            <Grid2 size={{ xs: 4 }}>
                              <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-mobilede`}>
                                <a>
                                  <Image
                                    alt="Mobile.De Logo"
                                    height={39}
                                    src="/images/promotion/mobile-de-logo-dark.svg"
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                    }}
                                    width={164}
                                  />
                                </a>
                              </Link>
                            </Grid2>
                            <Grid2 size={{ xs: 4 }}>
                              <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-ihk`}>
                                <a>
                                  <Image
                                    alt="IHK Logo"
                                    height={68}
                                    src="/images/promotion/ihk-logo-dark.svg"
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                    }}
                                    width={274}
                                  />
                                </a>
                              </Link>
                            </Grid2>
                            <Grid2 size={{ xs: 4 }}>
                              <Link href={`${t('components.contentTypes.PageHeaderBlock.urls.partner')}#scroll-automotivebusinessaward`}>
                                <a>
                                  <Image
                                    alt="KFZ Betrieb Logo"
                                    height={68}
                                    src="/images/promotion/kfz-gewerbe-logo-dark.svg"
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                    }}
                                    width={274}
                                  />
                                </a>
                              </Link>
                            </Grid2>
                          </Grid2>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                  {promotion1aAutoservice.isVisible && (
                    <Link href={promotion1aAutoservice.url}>
                      <Paper
                        sx={{
                          mt: 1,
                          py: 0.25,
                          px: 2,
                          fontSize: theme.typography.subtitle2.fontSize,
                          fontWeight: theme.typography.subtitle2.fontWeight,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        variant="outlined"
                      >
                        <Image
                          alt="1a autoservice"
                          height={20}
                          src="/images/promotion/1a-autoservice-logo.svg"
                          style={{ borderRadius: '4px' }}
                          width={20}
                        />
                        <Box ml={1}>
                          <Typography variant="subtitle2">
                            {t('components.contentTypes.PageHeaderBlock.1aAutomotiveTitle')}
                          </Typography>
                          <Typography variant="caption">
                            {t('components.contentTypes.PageHeaderBlock.1aAutomotiveSlogan')}
                          </Typography>
                        </Box>
                      </Paper>
                    </Link>
                  )}
                </Container>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
}

export default PageHeaderBlock;

function Slide(props) {
  const {
    enablePriority,
    isMobile,
    height,
    item,
    items,
    theme,
    isPartiallyCoveredByOtherElement,
  } = props;
  const {
    titlePosition,
    titleOffsetX,
    titleOffsetY,
    titleBackground,
    link,
    mobileMedia,
    textWidth,
  } = item;
  let { media } = item;
  const index = items.findIndex((i) => i.id === item.id);
  let title = null;
  let subtitle = null;
  let disclaimer = null;

  if (isMobile && mobileMedia) {
    media = mobileMedia;
  }

  if (item.title) {
    title = omitBy(item.title, isNull);
    defaults(title, {
      semanticVariant: 'div',
      displayVariant: 'h1',
    });
  }

  if (item.subtitle) {
    subtitle = omitBy(item.subtitle, isNull);
    defaults(subtitle, {
      semanticVariant: 'div',
      displayVariant: 'h5',
    });
  }

  if (item.disclaimer) {
    disclaimer = getDisclaimer(item.disclaimer);
  }

  const titleContainerStyle = {
    transform: `translate(${titleOffsetX ?? 0}%, ${titleOffsetY ?? 0}%)`,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        aspectRatio: `${item.media.file.data?.attributes.width ?? 1} / ${item.media.file.data?.attributes.height ?? 1}`,
        maxWidth: '100vw',
        [theme.breakpoints.up('md')]: {
          height,
          aspectRatio: 'auto',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          left: 0,
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: -1,
        }}
      >
        {item.media.file.data && (
          <MediaElement
            alt="slider image"
            data={media}
            format={isMobile ? 'medium' : 'original'}
            isAbsoluteFill
            priority={enablePriority && index === 0}
            style={{
              height: '100%',
            }}
          />
        )}
      </Box>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          overflow: 'hidden',
          pointerEvents: 'none',
          position: 'absolute',
          width: '100%',
          zIndex: 1,
          // paddingTop: HEADER_HEIGHT + theme.spacing(CONTENT_CONTAINER_PADDING_FACTOR),
          paddingTop: {
            xs: '10px',
            md: '180px',
          },
          '& * > p': {
            margin: 0,
          },
          maxHeight: {
            xs: 'none',
            md: height,
          },
          ...(getContentPosition(titlePosition)),
        }}
      >
        {(title || subtitle) && (
          <Box
            sx={{
              ...titleContainerStyle,
              maxWidth: getTextMaxWidth(textWidth),
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: getBackgroundColor(titleBackground, theme),
            }}
          >
            {subtitle && (
              <TypographyElement
                color="secondary"
                data={subtitle}
              />
            )}
            {title && (
              <TypographyElement
                color="secondary"
                data={title}
              />
            )}
          </Box>
        )}
        {disclaimer && (
          <Box
            sx={{
              display: {
                xs: 'none',
                md: 'block',
              },
              backdropFilter: 'saturate(180%) blur(3px)',
              paddingTop: theme.spacing(1),
              paddingBottom: isPartiallyCoveredByOtherElement ? '100px' : theme.spacing(1),
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              backgroundColor: 'rgba(0,0,0,0.35)',
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
              borderTopLeftRadius: 2,
              borderTopRightRadius: 2,
            }}
          >
            <TypographyElement
              color="secondary"
              data={disclaimer}
              sx={{
                lineHeight: '1.2em',
              }}
            />
          </Box>
        )}
      </Container>
      {link && (
        <LinkElement data={link}>
          {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
          <Box
            aria-label="slider link"
            component="a"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent',
            }}
          />
        </LinkElement>
      )}
    </Box>
  );
}

const CONTENT_MAX_WIDTH = 2000;
// const heightStyleMobile = 'calc(80vh - 45px)';
// const heightStyleDesktop = 'calc(100vh - 45px)';

function getContentPosition(titlePosition) {
  switch (titlePosition) {
    case 'TopLeft':
      return {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      };
    case 'TopCenter':
      return {
        justifyContent: 'flex-start',
        alignItems: 'center',
      };
    case 'TopRight':
      return {
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
      };
    case 'CenterLeft':
      return {
        justifyContent: 'center',
        alignItems: 'flex-start',
      };
    case 'CenterCenter':
      return {
        justifyContent: 'center',
        alignItems: 'center',
      };
    case 'CenterRight':
      return {
        justifyContent: 'center',
        alignItems: 'flex-end',
      };
    case 'BottomLeft':
      return {
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
      };
    case 'BottomCenter':
      return {
        justifyContent: 'flex-end',
        alignItems: 'center',
      };
    case 'BottomRight':
      return {
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
      };
    default:
      return {};
  }
}

function getTextMaxWidth(textWidth) {
  switch (textWidth) {
    case 'full':
      return '100%';
    case 'three_quarters':
      return '75%';
    case 'three_third':
      return '66.6%';
    case 'half':
      return '50%';
    case 'third':
      return '33.3%';
    case 'quarter':
      return '25%';
    default:
      return '100%';
  }
}

function getBackgroundColor(titleBackground, theme) {
  switch (titleBackground) {
    case 'primary':
      return theme.palette.primary.light;
    case 'light':
      return 'rgba(255,255,255,0.45)';
    case 'dark':
      return 'rgba(0,0,0,0.45)';
    default:
      return 'transparent';
  }
}

PageHeaderBlock.typeName = 'ComponentContentPageHeader'; // Strapi element type
PageHeaderBlock.propTypes = {
  data: PropTypes.shape({
    height: PropTypes.number,
    enableAutoplay: PropTypes.bool.isRequired,
    enablePromotionDisplay: PropTypes.bool.isRequired,
    pageHeaderSlides: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      titlePosition: PropTypes.string.isRequired,
      titleBackground: PropTypes.oneOf([
        null, 'default', 'primary', 'light', 'dark',
      ]),
      textWidth: PropTypes.oneOf([
        null, 'full', 'half', 'three_quarters', 'quarter',
      ]),
      link: LinkElement.propTypes,
      media: MediaElement.propTypes,
      mobileMedia: MediaElement.propTypes,
      title: TypographyElement.propTypes,
      subtitle: TypographyElement.propTypes,
      disclaimer: TypographyElement.propTypes,
    })),
  }).isRequired,
};

PageHeaderBlock.graphQlSchema = `
... on ${PageHeaderBlock.typeName} {
  id
  height
  enableAutoplay
  enablePromotionDisplay
  isContained
  pageHeaderVisibility : visibility
  pageHeaderSlides: slides(pagination: { limit: 100 }) {
    id
    titlePosition
    titleOffsetX
    titleOffsetY
    titleBackground
    textWidth
    link {
      ${LinkElement.graphQlSchema}
    }
    media {
      ${MediaElement.graphQlSchema}
    }
    mobileMedia {
      ${MediaElement.graphQlSchema}
    }
    title {
      ${TypographyElement.graphQlSchema}
    }
    subtitle {
      ${TypographyElement.graphQlSchema}
    }
    disclaimer {
      ${TypographyElement.graphQlSchema}
    }
  }
}
`;

function getDisclaimer(disclaimer) {
  disclaimer = omitBy(disclaimer, isNull);
  defaults(disclaimer, {
    semanticVariant: 'div',
    displayVariant: 'caption',
  });

  if (disclaimer?.value) {
    if (disclaimer.value.includes('</p>')) {
      const index = disclaimer.value.lastIndexOf('</p>');
      disclaimer.value = `${disclaimer.value.substring(0, index)} <sup>I.</sup>${disclaimer.value.substring(index, disclaimer.length)}`;
    } else {
      disclaimer.value += ' <sup>I.</sup>';
    }
  }

  return disclaimer;
}

function get1aAutoservicePromotion(router, t) {
  if (!router.asPath.startsWith('/unternehmen/standorte/berlin-spandau')) {
    return {
      isVisible: false,
    };
  }

  const url = t('components.contentTypes.PageHeaderBlock.urls.1aautomotive');

  return {
    isVisible: true,
    url,
  };
}
